import { Chart } from 'chart.js'
import { _DeepPartialObject } from 'chart.js/dist/types/utils'
import { BoxAnnotationOptions } from 'chartjs-plugin-annotation'

import { getFontSize } from '../../../utils/getFontSize'
import { Annotation } from '../../electricity/charts/priceChart/MarkedPeriodsDetails'
import { datasetDefaults } from '../../electricity/chartUtils/chartConfigs/initialChartOptions'
import { SpotPrice } from '../../../hooks/dayAheadPrices/useDayAheadPrices'
import { priceString } from '../../../utils/priceUtils'

const createHourpriceChart = (
	chartElement: HTMLCanvasElement,
	chartData: SpotPrice[],
	darkModeEnabled: boolean,
	annotations: Annotation[],
	showMarkedPeriods: boolean
) => {
	const fontSize = getFontSize()

	const dataPrices = chartData.map(data => data.centsPerKwh)
	const chartDataLabels = chartData.map(data => data.deliveryStart.hour())

	const colors = dataPrices.map((_, i) => {
		if (i === 0) {
			if (darkModeEnabled) {
				return '#144448'
			} else {
				return '#e3e3e3'
			}
		} else if (i === 1) {
			if (darkModeEnabled) {
				return '#acd7d4'
			} else {
				return '#127d27'
			}
		} else {
			if (darkModeEnabled) {
				return '#3cb49f'
			} else {
				return '#52ae32'
			}
		}
	})

	const tickColor = darkModeEnabled ? '#c8c8c8' : '#a3a3a3'
	const gridColor = darkModeEnabled ? '#144448' : '#f5f5f5'

	const hourPriceChart = new Chart(chartElement, {
		type: 'bar',
		data: {
			labels: chartDataLabels,
			datasets: [
				{
					...datasetDefaults,
					label: 'snt / kWh',
					backgroundColor: colors,
					data: dataPrices,
					barPercentage: 0.8,
					borderRadius: 2,
				},
			],
		},
		options: {
			devicePixelRatio: 2,
			layout: {
				padding: {
					left: 0,
					right: 0,
					top: 20,
					bottom: 0,
				},
			},
			scales: {
				y: {
					ticks: {
						font: {
							size: fontSize,
						},
						maxTicksLimit: 5,
						stepSize: 1,
						color: tickColor,
					},
					border: {
						display: false,
					},
					grid: {
						color: gridColor,
						drawTicks: false,
					},
				},
				x: {
					border: {
						display: false,
					},
					grid: {
						display: false,
					},
					ticks: {
						font: {
							size: fontSize,
						},
						autoSkip: false,
						minRotation: 45,
						maxRotation: 45,
						color: chartData.map((_, i) => {
							if (i === 0) {
								if (darkModeEnabled) {
									return '#c4c4c4'
								} else {
									return '#cccccc'
								}
							} else {
								if (darkModeEnabled) {
									return '#dcdcdc'
								} else {
									return '#666778'
								}
							}
						}),
						padding: -3,
						labelOffset: 5,
					},
				},
			},

			maintainAspectRatio: false,
			plugins: {
				tooltip: {
					titleFont: {
						size: fontSize + 2,
						weight: 'bold',
					},
					bodyFont: {
						size: fontSize,
					},
					callbacks: {
						title: (tooltipItems) => tooltipItems.map(i => `${i.label}:00 - ${parseInt(i.label) + 1}:00`),
						label: (item) => `${priceString(item.raw as number)} snt/kWh`,
					},
				},
				legend: {
					display: false,
				},
				annotation: {
					annotations: showMarkedPeriods
						? [
							...annotations.map(
								i =>
									({
										type: i.type,
										xMin: i.xMin, // shift left to include full bar area
										xMax: i.xMax, // shift right to include full bar area
										backgroundColor: i.backgroundColor,
										borderWidth: i.borderWidth,
										drawTime: i.drawTime,
									}) as _DeepPartialObject<BoxAnnotationOptions>
							),
						]
						: [],
				},
			},
		},
	})

	return hourPriceChart
}

export default createHourpriceChart
